import { call, put, takeLatest } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import api from "service/apiService";
import { ApiResponse } from "types";
import { translate } from "utils";
import { Post, Comment, UserCommunity } from "../types";
import {
	getPostsAsync,
	getPostDetailsAsync,
	GetPostsAsyncPayload,
	GetPostDetailsPayload,
	setPosts,
	setPost,
	updatePostAsync,
	UpdatePostPayload,
	CreatePostPayload,
	createPostAsync,
	GetCommentsAsyncPayload,
	getCommentsAsync,
	setPostComments,
	UpdateCommentPayload,
	updateCommentAsync,
	GetCommunityUsersAsyncPayload,
	getCommunityUsersAsync,
	setCommunityUsers,
} from "./post.slice";

// Get list community posts
function* getPostsSaga(action: PayloadAction<GetPostsAsyncPayload>) {
	const { query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Post[]>>(
				"/dashboard-posts",
				{ ...query },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data);
		yield put({ type: setPosts.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetPosts() {
	yield takeLatest(getPostsAsync, getPostsSaga);
}

// Get list community post's comments
function* getCommentsSaga(action: PayloadAction<GetCommentsAsyncPayload>) {
	const { postId, query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Comment[]>>(
				`/dashboard-posts/${postId}/comments`,
				{ ...query },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data);
		yield put({ type: setPostComments.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetComments() {
	yield takeLatest(getCommentsAsync, getCommentsSaga);
}

// Get list community users
function* getCommunityUsersSaga(action: PayloadAction<GetCommunityUsersAsyncPayload>) {
	const { users, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post<ApiResponse<UserCommunity[]>>(
				"/users/list",
				{
					users
				},
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data);
		yield put({ type: setCommunityUsers.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetCommunityUsers() {
	yield takeLatest(getCommunityUsersAsync, getCommunityUsersSaga);
}

// Get community post details
function* getPostDetailsSaga(action: PayloadAction<GetPostDetailsPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Post>>(
				`/dashboard-posts/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setPost.type, payload: response });
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchPostDetails() {
	yield takeLatest(getPostDetailsAsync, getPostDetailsSaga);
}

// Create new community post
function* createPostSaga(action: PayloadAction<CreatePostPayload>) {
	const { post, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post(
				"/dashboard-posts",
				{
					...post
				}
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate("Create successful"));
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}

function* watchCreatePost() {
	yield takeLatest(createPostAsync, createPostSaga);
}

// Update community post details
function* updatePostSaga(action: PayloadAction<UpdatePostPayload>) {
	const { id, post, willUpdate, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.put(
				`/dashboard-posts/${id}`,
				{
					...post
				}
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		if(willUpdate) {
			yield put({ type: setPost.type, payload: response.data });
		}
		message.success(translate("Update successful"));
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}


function* watchUpdatePost() {
	yield takeLatest(updatePostAsync, updatePostSaga);
}

// Update comment status
function* updateCommentSaga(action: PayloadAction<UpdateCommentPayload>) {
	const { id, status, callback, errorCallback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.patch(
				`/dashboard/comments/${id}/status`,
				{
					status
				}
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate("Update successful"));
		callback?.();
	} else {
		message.error(translate(error.message));
		errorCallback?.();
	}
}

function* watchUpdateComment() {
	yield takeLatest(updateCommentAsync, updateCommentSaga);
}

const postSaga = [
	watchGetPosts(),
	watchGetComments(),
	watchPostDetails(),
	watchCreatePost(),
	watchUpdatePost(),
	watchUpdateComment(),
	watchGetCommunityUsers()
];
export default postSaga;
